import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../lib/api";

/**
 * PresenceTracker — iter215
 *
 * Invisible, always-mounted presence pinger. Fires POST /api/presence/ping
 * every 30 s on ANY route (not just Landing + CampsiteDetail like the old
 * pill-based tracker), so real visitors browsing /wall, /community,
 * /browse, /compare, /share-your-trip, /secret-pitch-map, /admin etc.
 * finally show up in the humans counter.
 *
 * Design:
 *   - Zero UI. Renders `null`. The visible "X campers browsing" pill
 *     lives in <LiveBrowsingTicker /> and reads the same ping response.
 *   - Includes ?site_id=<id> when the current path matches /campsite/:id
 *     so the site-scoped viewer list keeps working.
 *   - Skips pings when the tab is hidden (battery + honest counts).
 *   - Silent on errors — never blocks the page.
 */
export default function PresenceTracker() {
  const location = useLocation();
  const timerRef = useRef(null);
  const pathRef = useRef(location.pathname);
  const tickRef = useRef(null);
  const firstPathRef = useRef(true);

  // Track the latest path in a ref so the 30-s interval always reads
  // the current route without needing to recreate the effect.
  pathRef.current = location.pathname;

  // Ping straight away on navigation so the admin sees the new page/campsite instantly.
  useEffect(() => {
    if (firstPathRef.current) { firstPathRef.current = false; return; }
    const t = setTimeout(() => tickRef.current && tickRef.current(), 400);
    return () => clearTimeout(t);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const currentSiteId = () => {
      const m = pathRef.current.match(/^\/campsite[s]?\/([^/?#]+)/);
      return m ? m[1] : null;
    };

    const tick = async () => {
      if (document.hidden) return;
      try {
        const siteId = currentSiteId();
        const params = new URLSearchParams({ path: pathRef.current });
        if (siteId) params.set("site_id", siteId);
        await api.post(`/presence/ping?${params.toString()}`);
      } catch {
        /* silent */
      }
      if (cancelled) return;
    };
    tickRef.current = tick;

    // First ping immediately on mount, then every 30 s.
    tick();
    timerRef.current = setInterval(tick, 30000);

    // Bonus: re-tick the moment the tab is refocused so "just returned"
    // visitors reappear instantly rather than waiting up to 30 s.
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
    // Fires once on app boot. Path changes are picked up via pathRef,
    // no need to re-run the effect on every navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
